import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useToggle } from 'usehooks-ts';

import { styled } from '@mui/material';

import trainerIcon from 'Assets/images/trainerIcon.svg';
import { ConfirmationDialog } from 'Common/ConfirmationDialog/ConfirmationDialog';
import {
  ConnectToFreshStartDesktopButton,
  ConnectToMyDesktopButton,
  ConnectToParticipantDesktopButton,
  ConnectToTrainerDesktopButton
} from 'Common/ConnectToDesktopButtons';
import type { CourseModel } from 'Courses/data';
import { Colors } from 'Themes/colors';
import type { UserDisplayModel } from 'User/userDisplayModel';
import { useBoolean } from 'Utils/customHooks';
import { openExternalUrl } from 'Utils/redirects';

import { FullscreenIcon as EnterFullscreenIcon, WebAssetOffIcon } from '@libs/shared/ui/icons';

import { useBasicFullscreenSafe } from '../../fullscreen-desktop-viewer/basic-fullscreen-context';
import { useDesktopFullscreenSafe } from '../../fullscreen-desktop-viewer/desktop-fullscreen-context';

import { useDesktopActions } from './hooks/use-desktop-actions';
import { LivePreview } from './live-preview/live-preview';
import { UserInfo } from './live-preview/user-info';
import { ActionButtons } from './action-buttons';
import { ActionWrapper, StyledCard } from './live-preview-card.styles';

import type { DesktopModel } from 'Api';

const StyledEnterFullscreenIcon = styled(EnterFullscreenIcon)`
  color: ${Colors.red} !important;
`;

const trainerAvatar = <img src={trainerIcon} alt="trainer icon" />;

type Props = {
  course: CourseModel;
  user: UserDisplayModel;
  desktop: DesktopModel;
  isFreshStartDesktop?: boolean;
  isParticipantDesktop?: boolean;
  isTrainerDesktop?: boolean;
  isTrainerDesktopPreview?: boolean;
  staticSize?: boolean;
  isHighlighted?: boolean;
  isMinimized?: boolean;
  participantsMachineControlActive?: boolean;
};

export const LivePreviewCard = ({
  course,
  user,
  desktop,
  isParticipantDesktop = false,
  isTrainerDesktop = false,
  isTrainerDesktopPreview = false,
  isFreshStartDesktop = false,
  staticSize = false,
  isHighlighted = false,
  isMinimized = false,
  participantsMachineControlActive = false
}: Readonly<Props>) => {
  const { t } = useTranslation();
  const [dialogIsDisplayed, displayDialog, hideDialog] = useBoolean();

  const cardRef = useRef<HTMLDivElement>(null);

  const desktopFullscreen = useDesktopFullscreenSafe();
  const basicFullscreen = useBasicFullscreenSafe();

  const hasDesktopSwitching = desktopFullscreen
    ? desktopFullscreen.switchableDesktops.length > 0
    : false;

  const desktopId = isFreshStartDesktop
    ? `freshstart-${course.id}`
    : isTrainerDesktop
      ? `trainer-${user.id}`
      : `participant-${user.id}`;

  const handleEnterFullscreen = () => {
    if (isTrainerDesktop && cardRef.current && desktop && basicFullscreen) {
      basicFullscreen.enterFullscreen(cardRef.current, desktop, course, user, {
        isFreshStartDesktop,
        isParticipantDesktop,
        isTrainerDesktop
      });
    } else if (hasDesktopSwitching && desktopFullscreen) {
      desktopFullscreen.enterFullscreen(desktopId);
    } else if (cardRef.current && desktop && basicFullscreen) {
      basicFullscreen.enterFullscreen(cardRef.current, desktop, course, user, {
        isFreshStartDesktop,
        isParticipantDesktop,
        isTrainerDesktop
      });
    }
  };

  const handleOpenPreviewOnNewTab = () => {
    const link = isTrainerDesktopPreview
      ? (desktop?.viewLink as string)
      : (desktop?.link as string);

    if (link) {
      openExternalUrl(link);
    }
  };

  const { stopMachine, removeMachine } = useDesktopActions();

  const handleStopMachine = () => {
    stopMachine({
      course,
      user,
      desktop,
      isFreshStartDesktop,
      isParticipantDesktop,
      isTrainerDesktop
    });
    toggleSettings();
  };

  const handleRemoveMachine = () => {
    removeMachine({
      course,
      user,
      desktop,
      isFreshStartDesktop,
      isParticipantDesktop,
      isTrainerDesktop
    });
    hideDialog();
    toggleSettings();
  };

  const FullscreenIcon = StyledEnterFullscreenIcon;

  const [settingsOpened, toggleSettings] = useToggle();

  let connectToDesktopButton: React.ReactElement | null = null;

  if (isFreshStartDesktop) {
    connectToDesktopButton = (
      <ConnectToFreshStartDesktopButton
        courseId={course.id}
        isFreshStartMachineReadyToClone={course.isFreshStartMachineReadyToClone}
      />
    );
  } else if (isParticipantDesktop) {
    connectToDesktopButton = (
      <ConnectToParticipantDesktopButton
        courseId={course.id}
        participantId={user.id as string}
        isHighlighted={isHighlighted}
        isMinimized={isMinimized}
      />
    );
  } else if (isTrainerDesktop) {
    if (course.isTrainer) {
      connectToDesktopButton = (
        <ConnectToMyDesktopButton
          courseId={course.id}
          isHighlighted={isHighlighted}
          isMinimized={isMinimized}
        />
      );
    } else {
      connectToDesktopButton = (
        <ConnectToTrainerDesktopButton
          courseId={course.id}
          isHighlighted={isHighlighted}
          isMinimized={isMinimized}
        />
      );
    }
  } else if (!isTrainerDesktopPreview) {
    connectToDesktopButton = (
      <ConnectToMyDesktopButton
        courseId={course.id}
        isHighlighted={isHighlighted}
        isMinimized={isMinimized}
      />
    );
  }

  let componentToRender: React.ReactElement | null = null;

  const hasInstanceId = !!desktop?.instanceId;
  const shouldShowConnectToDesktopButton =
    hasInstanceId ||
    (!desktop?.isRunning && course.hasFreshStart && course.isFreshStartMachineReadyToClone);

  if (shouldShowConnectToDesktopButton) {
    componentToRender = connectToDesktopButton;
  } else {
    componentToRender = <WebAssetOffIcon fontSize="large" />;
  }

  return (
    <>
      <StyledCard
        ref={cardRef}
        fullscreenModeIsEnabled={false}
        staticSize={staticSize}
        settingsOpened={settingsOpened}
        isMinimized={isMinimized}
      >
        {!isMinimized ? (
          <LivePreview
            fullscreenModeIsEnabled={false}
            settingsOpened={settingsOpened}
            course={course}
            desktop={desktop}
            isParticipantDesktop={isParticipantDesktop}
            isTrainerDesktop={isTrainerDesktop}
            isTrainerDesktopPreview={isTrainerDesktopPreview}
            connectToDesktopButton={connectToDesktopButton}
            participantsMachineControlActive={participantsMachineControlActive}
          />
        ) : null}

        <ActionWrapper fullScreen={false} pinEnabled={true} isMinimized={isMinimized}>
          {!desktopFullscreen?.isFullscreen && !basicFullscreen?.isFullscreen && (
            <UserInfo
              name={user.displayName}
              avatarColor={user.avatarColor}
              avatar={user.avatar}
              customAvatar={
                isTrainerDesktopPreview || isTrainerDesktop ? trainerAvatar : user.customAvatar
              }
            />
          )}

          {isMinimized ? componentToRender : null}

          {!isMinimized || shouldShowConnectToDesktopButton ? (
            <ActionButtons
              desktop={desktop}
              isFullscreenEnabled={false}
              isTrainerDesktopPreview={isTrainerDesktopPreview}
              canControlMachine={
                !isTrainerDesktop ||
                (course.isTrainer ?? false) ||
                (!(course.isParticipant ?? true) && !(course.isTrainer ?? false))
              }
              settingsOpened={settingsOpened}
              toggleSettings={toggleSettings}
              handleOpenPreviewOnNewTab={handleOpenPreviewOnNewTab}
              handleStopMachine={handleStopMachine}
              displayDialog={displayDialog}
              FullscreenIcon={FullscreenIcon}
              toggleFullscreenMode={handleEnterFullscreen}
            />
          ) : null}
        </ActionWrapper>
      </StyledCard>

      <ConfirmationDialog
        open={dialogIsDisplayed}
        content={t('RemoveMachineQuestion', { instanceName: desktop?.instanceName })}
        onNo={hideDialog}
        onYes={handleRemoveMachine}
      />
    </>
  );
};
